'use client';

import { useState, useEffect, useCallback } from 'react';

import Cookies from 'js-cookie';

import { trackUserBucketedEvent } from '@/analytics/trackEvent';
import { safeLocalStorage } from '@/lib/safeLocalStorage';

export type Flags = Record<string, string>;

export function useCohort() {
  const [cohort, setCohort] = useState<string>('');

  useEffect(() => {
    const cohort = Cookies.get('ff-cohort');
    if (cohort) {
      setCohort(cohort);
    }
  }, [cohort]);

  return cohort;
}

export function useFlagID() {
  const [flagID, setFlagID] = useState<string>('');

  useEffect(() => {
    const storedFlagID = Cookies.get('ff-feature-uid');
    if (storedFlagID) {
      setFlagID(storedFlagID);
    }
  }, [flagID]);

  return flagID;
}

export function useFlags() {
  const [flags, setFlags] = useState<Flags>({});
  const [showFlagsUI, setShowFlagsUI] = useState(false);
  const ID = useFlagID();
  const cohort = useCohort();

  useEffect(() => {
    (async () => {
      const storedFlags = JSON.parse(Cookies.get('ff-feature-flags') || '{}');
      const showFeatureFlagsUI =
        safeLocalStorage.getItem('showFeatureFlags') === 'true';

      setShowFlagsUI(showFeatureFlagsUI);
      if (storedFlags) {
        setFlags(storedFlags);
      }
    })();
  }, []);

  const variantEnabled = useCallback(
    (feature: string) => {
      let variant;
      if (Object.keys(flags).length === 0) {
        const storedFlags = JSON.parse(Cookies.get('ff-feature-flags') || '{}');
        variant = storedFlags[feature];
      } else {
        variant = flags[feature];
      }

      if (!variant) {
        return false; // fail closed
      }

      trackUserBucketedEvent(variant, feature, flags);
      return flags[feature];
    },
    [flags],
  );

  const updateFlags = useCallback(async (ID: string, newFlags: Flags) => {
    setFlags(newFlags);
    fetch('/api/features', {
      method: 'POST',
      body: JSON.stringify({
        ID,
        flags: newFlags,
      }),
    }).then(() => {
      location.reload();
    });
  }, []);

  const enableFlagsUI = useCallback(() => {
    safeLocalStorage.setItem('showFeatureFlags', 'true');
    setShowFlagsUI(true);
  }, []);

  return {
    cohort,
    ID,
    flags,
    variantEnabled,
    updateFlags,
    showFlagsUI,
    enableFlagsUI,
  };
}
